import {
    blueBlackShade,
    blueishWhiteColor,
    blueWhiteShade,
    slightBluishTintColor,
    slightSoftWhiteBlueShade,
    systemColor,
    white,
    blueStrongShade,
    lightWhiteRedShade,
    lightGreenShade,
    strongGreenColorShade,
    lightRedShade,
    lightYellowShade,
    redShadeStrong,
    brownStrong,
    lightBlueShade,
    strongBrown,
    whiteSlightBlue,
    darkBlueStrong,
    slightBlueishWhite,
    whiteStrongShadeColor,
    whiteShadeColor,
} from "../../../config/ColorObj";

// Keep in sync with COL_IDX_W / COL_PROD_W constants in the JSX
const COL_IDX_W = 52;
const COL_PROD_W = 185;

export const styles = {
    containerCenter: {
        display: "flex",
        justifyContent: "center",
        marginTop: "6px",
    },

    tableWrapper: {
        background: white,
        borderRadius: "10px",
        border: "1px solid #e0e0e0",
        width: "100%",
        maxHeight: "570px",
        overflow: "auto",
    },

    table: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
        fontSize: "14px",
        minWidth: "820px",
    },

    thead: {
        background: blueishWhiteColor,
        position: "sticky",
        top: 0,
        zIndex: 4,
    },

    th: {
        padding: "14px 16px",
        fontSize: "12px",
        fontWeight: "600",
        color: white,
        textTransform: "uppercase",
        borderBottom: "2px solid #e5e7eb",
        background: systemColor,
    },

    // ── Frozen column helpers ──────────────────────────────────────────────
    // Applied on th / td in addition to base th/td style.
    // left offset is set inline in JSX using COL_IDX_W / COL_PROD_W constants.

    stickyIdx: {
        position: "sticky",
        left: 0,
        zIndex: 3,
        background: white,
        width: COL_IDX_W,
        minWidth: COL_IDX_W,
    },

    stickyProd: {
        position: "sticky",
        left: COL_IDX_W,   // immediately after # column
        zIndex: 3,
        background: white,
        width: COL_PROD_W,
        minWidth: COL_PROD_W,
        // right freeze border — declared here as default; JSX overrides to "2px solid #d1d5db" for the visible separator
    },

    /*
     * frozenExpandPlaceholder — used in ExpandPanel rows for the two
     * frozen cells that keep the expand panel content out of the frozen zone.
     * Must be sticky + correct z-index so they overlay the scrolling content.
     */
    frozenExpandPlaceholder: {
        position: "sticky",
        zIndex: 2,
        background: white,
        padding: 0,
        borderBottom: "none",
        // No border-bottom so the animated panel row looks seamless
    },

    // ── Regular cells ──────────────────────────────────────────────────────

    td: {
        padding: "12px 16px",
        borderBottom: "1px solid #f1f5f9",
        color: blueWhiteShade,
        background: white,
    },

    productCell: {
        minWidth: COL_PROD_W,
    },

    productName: {
        fontWeight: "600",
        fontSize: "13px",
        color: blueBlackShade,
    },

    productSubtitle: {
        fontSize: "11px",
        color: slightBluishTintColor,
        marginTop: "2px",
    },

    indexCell: {
        textAlign: "center",
        fontWeight: "600",
        color: slightBluishTintColor,
    },

    number: {
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
    },

    groupDivider: {
        borderBottom: "2px solid #e5e7eb",
        padding: 0,
    },

    totalRow: {
        background: lightWhiteRedShade,
        fontWeight: "600",
    },

    // ── Badges ─────────────────────────────────────────────────────────────

    badge: {
        padding: "4px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block",
    },

    badgeTypes: {
        challan: { background: slightSoftWhiteBlueShade, color: blueStrongShade },

        sell: { background: lightGreenShade, color: strongGreenColorShade },

        remaining: (v) => ({
            background: v <= 0 ? lightRedShade : lightYellowShade,
            color: v <= 0 ? redShadeStrong : brownStrong,
        }),

        //NEW
        challanStatus: (status) => {
            const s = (status || "").toLowerCase();

            if (s === "closed") {
                return {
                    background: lightGreenShade,   // light green
                    color: strongGreenColorShade,
                };
            }

            if (s === "pending") {
                return {
                    background: lightBlueShade,   // light brown/yellow
                    color: strongBrown,
                };
            }

            if (s === "active") {
                return {
                    background: slightBlueishWhite,   // light blue
                    color: darkBlueStrong,
                };
            }

            // fallback (important)
            return {
                background: whiteSlightBlue,
                color: blueWhiteShade,
            };
        },
    },

    // ── Progress bar ───────────────────────────────────────────────────────

    progressWrap: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        minWidth: "140px",
    },

    progressTrack: {
        flex: 1,
        height: "6px",
        background: whiteStrongShadeColor,
        borderRadius: "999px",
        overflow: "hidden",
    },

    progressFill: {
        height: "100%",
        borderRadius: "999px",
        transition: "width 0.3s ease",
    },

    progressPct: {
        fontSize: "11px",
        color: slightBluishTintColor,
        minWidth: "30px",
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
    },

    // ── Expand button ──────────────────────────────────────────────────────

    expandBtn: {
        width: "28px",
        height: "28px",
        borderRadius: "20px",
        border: "1px solid #e5e7eb",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.15s, color 0.15s",
        outline: "none",
    },

    // ── Expand panel animation ─────────────────────────────────────────────

    expandInner: {
        overflow: "hidden",
        transition: "max-height 0.3s ease, opacity 0.3s ease",
    },

    // Sub-table is indented to align under the Challan column (skips frozen zone)
    outletTableWrap: {
        padding: "10px 20px 14px 16px",
        background: whiteShadeColor,
        borderBottom: "1px solid #e5e7eb",
    },

    // ── Outlet sub-table ───────────────────────────────────────────────────

    outletTable: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "12px",
    },

    outletTh: {
        textAlign: "left",
        fontSize: "11px",
        fontWeight: "600",
        color: slightBluishTintColor,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        padding: "6px 12px",
        borderBottom: "1px solid #e5e7eb",
        background: "transparent",
    },

    outletTd: {
        padding: "7px 12px",
        borderBottom: "1px solid #f1f5f9",
        color: blueWhiteShade,
        fontSize: "12px",
    },

    noOutletText: {
        fontSize: "12px",
        color: slightBluishTintColor,
        padding: "8px 0",
    },

    // ── Search ─────────────────────────────────────────────────────────────

    searchBox: {
        width: "85%",
        marginLeft: "5px",
        display: "flex",
        justifyContent: "flex-start",
        marginTop: "-25px",
    },

    input: {
        padding: "8px 12px",
        borderRadius: "6px",
        border: "1px solid #d1d5db",
        outline: "none",
        fontSize: "14px",
        width: "250px",
    },
};
